import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { can } from '../../utils/permissions';
import { resolveLinkedRecordNav } from '../../utils/linkedRecordNav';
import { displayName } from '../../utils/userLabel';
import {
  formatLocalMoment, fromLocalInput, localIsoWithOffset, offsetMinutes, parseSpecimenCode, toLocalInput,
} from '../../utils/specimenCode';
import { useAuth } from '../../context/AuthContext';
import QRCodeLabel from '../../components/QRCodeLabel';
import AuditTrail from '../../components/specimens/AuditTrail';
import ProvenanceGraph from '../../components/specimens/ProvenanceGraph';
import StageEditor from '../../components/specimens/StageEditor';
import StoragePanel from '../../components/specimens/StoragePanel';
import LabCodeEditor from '../../components/specimens/LabCodeEditor';
import { currentFix } from '../../components/specimens/SpecimenOnSite';
import * as S from '../../components/specimens/styles';

/*
 * One specimen ("isolate details"): its code and QR, what is known about its
 * collection, its storage, its lifecycle as a graph, and its history.
 *
 * Everything shown comes from GET /specimens/{id}/graph, which carries the
 * specimen too; every change answers with the new graph, so the page is
 * redrawn from what the server now holds rather than patched locally. The
 * history below is re-read after every change.
 */

export default function SpecimenDetail() {
  const { specimenId } = useParams();
  const { t } = useTranslation(['specimens', 'common']);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [graph, setGraph] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [selected, setSelected] = useState(null);
  const [adding, setAdding] = useState(null);       // {after, before} while adding a stage
  const [showRemoved, setShowRemoved] = useState(false);
  const [editingFacts, setEditingFacts] = useState(false);

  const load = useCallback(() => {
    api.get(`/specimens/${specimenId}/graph`, { params: showRemoved ? { include_removed: 1 } : {} })
      .then((res) => { setGraph(res.data); setLoadError(''); })
      .catch((e) => setLoadError(e.response?.status === 404 ? t('detail.notFound') : errorText(e)));
  }, [specimenId, showRemoved, t]);
  useEffect(() => { load(); }, [load]);

  /** Run a change; on success take the server's answer and re-read the history. */
  const change = async (key, request, { reloadGraph = false } = {}) => {
    setBusy(key);
    setError((e) => ({ ...e, [key]: '' }));
    try {
      const res = await request();
      if (res.data?.stages) setGraph((g) => ({ ...res.data, specimen: res.data.specimen || g.specimen }));
      else if (res.data?.specimen_id) setGraph((g) => ({ ...g, specimen: res.data }));
      if (reloadGraph) load();
      setRefreshKey((k) => k + 1);
      return true;
    } catch (e) {
      setError((all) => ({ ...all, [key]: errorText(e) }));
      return false;
    } finally {
      setBusy('');
    }
  };

  if (loadError) {
    return (
      <div style={{ padding: 24 }}>
        <p style={S.error} role="alert">{loadError}</p>
        <Link to="/specimens">{t('detail.back')}</Link>
      </div>
    );
  }
  if (!graph) return <div style={{ padding: 24 }}>{t('loading')}</div>;

  const s = graph.specimen;
  const canWrite = can(user, 'specimen:write');
  const canManage = can(user, 'specimen:manage');
  const active = s.status === 'active';
  const stageTitle = (stage) => (stage.title_is_default ? t(`stage.types.${stage.stage_type}`) : stage.title);
  const selectedStage = graph.stages.find((st) => st.stage_id === selected) || null;
  const codeParts = parseSpecimenCode(s.specimen_code);
  const mismatched = Object.entries(s.code_basis || {}).filter(([, ok]) => ok === false).map(([part]) => part);

  const saveSpecimen = (body, key = 'facts') => change(key, () => api.put(`/specimens/${s.specimen_id}`, body),
    { reloadGraph: true });
  const saveStage = (payload) => {
    if (adding) {
      return change('stage', () => api.post(`/specimens/${s.specimen_id}/stages`, payload)).then((ok) => {
        if (ok) setAdding(null);
      });
    }
    return change('stage', () => api.put(`/specimens/${s.specimen_id}/stages/${selected}`, payload));
  };
  const removeStage = (reason) => change('stage', () => api.post(
    `/specimens/${s.specimen_id}/stages/${selected}/remove`, { reason })).then((ok) => { if (ok) setSelected(null); });

  // A collection opens on its own page, an intake in the home hub's drawer.
  const originNav = resolveLinkedRecordNav(s.origin_table, s.sample_id || s.external_id);

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      <Link to="/specimens" style={{ fontSize: 14 }}>← {t('detail.back')}</Link>

      {/* ── header: the code, its QR, its state ─────────────────────── */}
      <section style={{ ...S.card, display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* The QR is the Isolate ID; the printed label also carries the short lab code. */}
        <QRCodeLabel value={s.specimen_code} size={120}
          caption={[s.lab_code, s.taxon_name].filter(Boolean).join(' · ')} />
        <div style={{ flex: '1 1 260px' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={S.pill(s.status)} data-testid="specimen-status">{t(`status.${s.status}`)}</span>
            <span style={S.hint}>{t(`minted.${s.minted_via}`)}</span>
          </div>
          {s.lab_code && (
            <p style={{ margin: '8px 0 0', fontSize: 15 }} data-testid="specimen-lab-code" title={t('detail.tip.labCode')}>
              {t('fields.labCode')}:{' '}
              <bdi dir="ltr" style={{ ...S.mono, fontWeight: 700 }}>{s.lab_code}</bdi>
            </p>
          )}
          {/* The bank's own code, set by hand with a reason (0083). */}
          {canWrite && active && (
            <div style={{ marginTop: 8 }}>
              <LabCodeEditor compact specimenId={s.specimen_id} labCode={s.lab_code} canEdit
                onSave={(body) => saveSpecimen(body, 'labCode')} />
              {error.labCode && <p role="alert" style={S.error}>{error.labCode}</p>}
            </div>
          )}
          {codeParts && (
            <p style={{ ...S.hint, margin: '8px 0 0' }} data-testid="code-reading">
              {t('detail.codeReads', { date: codeParts.date, time: codeParts.time, location: codeParts.location, seq: codeParts.seq })}
              {' '}{t(`locationSource.${s.location_source}`)}
            </p>
          )}
        </div>
        <Lifecycle specimen={s} user={user} canManage={canManage} busy={busy === 'life'} error={error.life}
          t={t} onAction={(action, reason) => change('life', () => api.post(`/specimens/${s.specimen_id}/${action}`, { reason }),
            { reloadGraph: true })} />
      </section>

      {mismatched.length > 0 && (
        <p style={{ ...S.warning, marginTop: 12 }} data-testid="integrity-notice" role="note">
          {t('detail.integrity', { parts: mismatched.map((p) => t(`detail.parts.${p}`)).join(', ') })}
        </p>
      )}

      {s.status === 'reserved' && (
        <AssignPanel specimen={s} t={t} busy={busy === 'assign'} error={error.assign}
          onAssign={(body) => change('assign', () => api.post(`/specimens/${s.specimen_id}/assign`, body), { reloadGraph: true })} />
      )}

      {/* ── what is known ───────────────────────────────────────────── */}
      {s.status !== 'reserved' && s.status !== 'void' && (
        <section style={S.card} aria-labelledby="facts-title">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h3 id="facts-title" style={S.sectionTitle}>{t('detail.facts')}</h3>
            {canWrite && active && !editingFacts && (
              <button type="button" style={S.ghostBtn} onClick={() => setEditingFacts(true)} title={t('detail.tip.edit')}
                data-testid="facts-edit">{t('detail.edit')}</button>
            )}
          </div>
          {!editingFacts && (
            <dl style={S.dl}>
              <dt style={S.dt}>{t('fields.collected')}</dt>
              <dd style={S.dd}>
                {formatLocalMoment(s.collected_at_local, s.time_known) || '—'}
                {!s.time_known && <span style={S.hint}> · {t('detail.timeUnknown')}</span>}
              </dd>
              <dt style={S.dt}>{t('fields.locationName')}</dt><dd style={S.dd}>{s.location_name || '—'}</dd>
              <dt style={S.dt}>{t('fields.coordinates')}</dt>
              <dd style={S.dd} dir="ltr">{s.gps_lat != null ? `${s.gps_lat}, ${s.gps_lng}` : '—'}</dd>
              <dt style={S.dt}>{t('fields.taxon')}</dt>
              <dd style={{ ...S.dd, fontStyle: s.taxon_name ? 'italic' : 'normal' }}>{s.taxon_name || '—'}</dd>
              <dt style={S.dt}>{t('fields.collector')}</dt><dd style={S.dd}>{displayName(s.collector_name, t) || '—'}</dd>
              <dt style={S.dt}>{t('fields.origin')}</dt>
              <dd style={S.dd}>
                {s.origin_table ? (
                  <button type="button" onClick={() => originNav && navigate(originNav.path, { state: originNav.state })}
                    style={{ ...S.mono, background: 'none', border: 'none', padding: 0, color: 'var(--c-primary-fg)',
                      cursor: 'pointer', fontWeight: 600 }} title={t('detail.tip.origin')} data-testid="origin-link">
                    {s.origin_code || t(`record.${s.origin_table}`)}
                  </button>
                ) : '—'}
                {s.origin_deleted && <span style={S.hint}> ({t('graph.deleted')})</span>}
              </dd>
              <dt style={S.dt}>{t('fields.strain')}</dt><dd style={{ ...S.dd, ...S.mono }}>{s.strain_code || '—'}</dd>
              <dt style={S.dt}>{t('fields.registered')}</dt>
              <dd style={S.dd}>{t('detail.registeredBy', { who: displayName(s.created_by_name, t) || '—', when: String(s.created_at).slice(0, 16).replace('T', ' ') })}</dd>
              {s.reserved_at && (<>
                <dt style={S.dt}>{t('fields.printed')}</dt>
                <dd style={S.dd}>{t('detail.printedBy', { who: displayName(s.reserved_by_name, t) || '—', when: String(s.reserved_at).slice(0, 10) })}
                  {s.assigned_at && ` · ${t('detail.assignedBy', { who: displayName(s.assigned_by_name, t) || '—', when: String(s.assigned_at).slice(0, 10) })}`}
                </dd>
              </>)}
              {s.notes && (<><dt style={S.dt}>{t('fields.notes')}</dt><dd style={{ ...S.dd, whiteSpace: 'pre-wrap' }}>{s.notes}</dd></>)}
            </dl>
          )}
          {editingFacts && (
            <FactsEditor specimen={s} t={t} busy={busy === 'facts'} error={error.facts}
              onCancel={() => setEditingFacts(false)}
              onSave={async (body) => { if (await saveSpecimen(body)) setEditingFacts(false); }} />
          )}
        </section>
      )}

      {s.status !== 'reserved' && s.status !== 'void' && (
        <StoragePanel specimen={s} canEdit={canWrite && active} busy={busy === 'storage'} error={error.storage} t={t}
          onSave={(body) => saveSpecimen(body, 'storage')} />
      )}

      {/* ── the workflow graph ──────────────────────────────────────── */}
      {graph.stages.length > 0 && (
        <section style={S.card} aria-labelledby="graph-title">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h3 id="graph-title" style={S.sectionTitle}>{t('graph.title')}</h3>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                <input type="checkbox" checked={showRemoved} onChange={(e) => setShowRemoved(e.target.checked)}
                  title={t('graph.tip.showRemoved')} />
                {t('graph.showRemoved')}
              </label>
              {canWrite && active && (
                <button type="button" style={S.btn()} data-testid="graph-add" title={t('graph.tip.add')}
                  onClick={() => { setSelected(null); setAdding(selected ? { after: selected } : {}); }}>
                  {t('graph.add')}
                </button>
              )}
            </div>
          </div>
          <p style={S.hint}>{canWrite && active ? t('graph.introEdit') : t('graph.intro')}</p>
          {error.graph && <p style={S.error} role="alert">{error.graph}</p>}
          <ProvenanceGraph stages={graph.stages.filter((st) => showRemoved || !st.removed_at)} edges={graph.edges}
            linked={graph.linked} selectedId={selected} canEdit={canWrite && active} stageTitle={stageTitle} t={t}
            onSelect={(id) => { setAdding(null); setSelected(id === selected ? null : id); }}
            onAppend={(id) => { setSelected(null); setAdding({ after: id }); }}
            onInsert={(edge) => { setSelected(null); setAdding({ after: edge.from, before: edge.to }); }}
            onConnect={(from, to) => change('graph', () => api.post(`/specimens/${s.specimen_id}/edges`,
              { from_stage_id: from, to_stage_id: to }))}
            onRemoveEdge={(edge) => change('graph', () => api.delete(`/specimens/${s.specimen_id}/edges/${edge.id}`))} />
          {(adding || selectedStage) && (
            <StageEditor stage={adding ? null : selectedStage} placement={adding} linked={graph.linked}
              specimen={s} stageTitle={stageTitle} busy={busy === 'stage'} error={error.stage} t={t}
              onSave={canWrite && active ? saveStage : undefined}
              onRemove={canWrite && active ? removeStage : undefined}
              onCancel={() => { setAdding(null); setSelected(null); }} />
          )}
        </section>
      )}

      <AuditTrail specimenId={s.specimen_id} refreshKey={refreshKey} t={t} />
    </div>
  );
}

/* Archive (when the server says this caller may: `actions.archive`,
 * routes/ownership.py -- a system admin, a collection manager, or whoever
 * registered it while the lab has done nothing with it), restore (managers),
 * void a blank label (managers, or whoever printed it). */
function Lifecycle({ specimen, user, canManage, busy, error, onAction, t }) {
  const [action, setAction] = useState(null);
  const [reason, setReason] = useState('');
  const options = [];
  if (specimen.actions?.archive && specimen.status === 'active') options.push('archive');
  if (canManage && specimen.status === 'archived') options.push('restore');
  if (specimen.status === 'reserved' && (canManage || specimen.reserved_by === user?.user_id)) options.push('void');
  if (options.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 220 }}>
      {!action && options.map((a) => (
        <button key={a} type="button" onClick={() => setAction(a)} data-testid={`life-${a}`}
          style={a === 'restore' ? S.btn() : S.btn('var(--c-danger)')} title={t(`life.tip.${a}`)}>{t(`life.${a}`)}</button>
      ))}
      {action && (
        <form onSubmit={async (e) => { e.preventDefault(); if (await onAction(action, reason.trim())) { setAction(null); setReason(''); } }}
          style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={S.label}>
            {t(`life.reason.${action}`)}
            <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} style={S.input}
              title={t('life.tip.reason')} data-testid="life-reason" autoFocus />
          </label>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="submit" disabled={busy || reason.trim().length < 3} style={S.btn('var(--c-danger)')}
              title={t('life.tip.confirm')} data-testid="life-confirm">{t('life.confirm')}</button>
            <button type="button" onClick={() => { setAction(null); setReason(''); }} style={S.ghostBtn}
              title={t('tip.cancel')}>{t('cancel')}</button>
          </div>
        </form>
      )}
      {error && <p style={S.error} role="alert">{error}</p>}
    </div>
  );
}

/* Correct what is recorded. A change to when or where needs a reason. */
function FactsEditor({ specimen, busy, error, onSave, onCancel, t }) {
  const [form, setForm] = useState({
    when: toLocalInput(specimen.collected_at_local), dateOnly: !specimen.time_known,
    gps_lat: specimen.gps_lat ?? '', gps_lng: specimen.gps_lng ?? '',
    location_name: specimen.location_name || '', taxon_name: specimen.taxon_name || '',
    collector_name: specimen.collector_name || '', notes: specimen.notes || '', reason: '',
  });
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const originalWhen = toLocalInput(specimen.collected_at_local);
  const whenChanged = form.when !== originalWhen || form.dateOnly !== !specimen.time_known;
  const placeChanged = String(form.gps_lat) !== String(specimen.gps_lat ?? '') || String(form.gps_lng) !== String(specimen.gps_lng ?? '');

  const submit = (e) => {
    e.preventDefault();
    const body = {};
    if (whenChanged) {
      const raw = form.dateOnly ? form.when.slice(0, 10) : form.when;
      body.collected_at = fromLocalInput(raw);
      body.utc_offset_minutes = offsetMinutes(new Date(form.dateOnly ? `${raw}T12:00` : raw));
      if (form.dateOnly) body.time_known = false;
    }
    if (placeChanged) {
      body.gps_lat = form.gps_lat === '' ? null : Number(form.gps_lat);
      body.gps_lng = form.gps_lng === '' ? null : Number(form.gps_lng);
    }
    ['location_name', 'taxon_name', 'collector_name', 'notes'].forEach((k) => {
      if (form[k] !== (specimen[k] || '')) body[k] = form[k];
    });
    if (form.reason.trim()) body.reason = form.reason.trim();
    onSave(body);
  };

  return (
    <form onSubmit={submit} data-testid="facts-editor">
      <p style={S.hint}>{t('detail.editHint')}</p>
      <div style={S.row}>
        <label style={{ ...S.label, flex: '1 1 220px' }}>
          {t('fields.collected')}
          <input type={form.dateOnly ? 'date' : 'datetime-local'} value={form.dateOnly ? form.when.slice(0, 10) : form.when}
            onChange={set('when')} style={S.input} title={t('register.tip.when')} data-testid="facts-when" />
        </label>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 14, paddingBottom: 8 }}>
          <input type="checkbox" checked={form.dateOnly} onChange={set('dateOnly')} title={t('register.tip.dateOnly')} />
          {t('register.dateOnly')}
        </label>
        <label style={{ ...S.label, flex: '1 1 120px' }}>
          {t('fields.lat')}
          <input type="number" step="any" value={form.gps_lat} onChange={set('gps_lat')} dir="ltr" style={S.input}
            title={t('tip.lat')} data-testid="facts-lat" />
        </label>
        <label style={{ ...S.label, flex: '1 1 120px' }}>
          {t('fields.lng')}
          <input type="number" step="any" value={form.gps_lng} onChange={set('gps_lng')} dir="ltr" style={S.input}
            title={t('tip.lng')} data-testid="facts-lng" />
        </label>
      </div>
      <div style={{ ...S.row, marginTop: 10 }}>
        {['location_name', 'taxon_name', 'collector_name'].map((k) => (
          <label key={k} style={{ ...S.label, flex: '1 1 200px' }}>
            {t(`fields.${{ location_name: 'locationName', taxon_name: 'taxon', collector_name: 'collector' }[k]}`)}
            <input value={form[k]} onChange={set(k)} style={S.input} title={t(`tip.${{ location_name: 'locationName', taxon_name: 'taxon', collector_name: 'collector' }[k]}`)} />
          </label>
        ))}
      </div>
      <label style={{ ...S.label, marginTop: 10 }}>
        {t('fields.notes')}
        <textarea value={form.notes} onChange={set('notes')} rows={2} style={S.input} title={t('tip.notes')} />
      </label>
      <label style={{ ...S.label, marginTop: 10 }}>
        {t('detail.reason')}{(whenChanged || placeChanged) && <span style={{ color: 'var(--c-danger-fg)' }}> *</span>}
        <input value={form.reason} onChange={set('reason')} maxLength={2000} style={S.input} title={t('detail.tip.reason')}
          data-testid="facts-reason" required={whenChanged || placeChanged} minLength={3} />
        <span style={S.hint}>{t('detail.reasonHint')}</span>
      </label>
      {error && <p style={S.error} role="alert">{error}</p>}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button type="submit" disabled={busy} style={S.btn()} title={t('detail.tip.save')} data-testid="facts-save">
          {busy ? t('saving') : t('detail.save')}</button>
        <button type="button" style={S.ghostBtn} onClick={onCancel} title={t('tip.cancel')}>{t('cancel')}</button>
      </div>
    </form>
  );
}

/* A pre-printed label, put on a specimen: now and here by default. */
function AssignPanel({ specimen, busy, error, onAssign, t }) {
  const [form, setForm] = useState({ when: toLocalInput(localIsoWithOffset()), taxon_name: '', location_name: '' });
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    const fix = await currentFix();
    onAssign({
      collected_at: fromLocalInput(form.when), utc_offset_minutes: offsetMinutes(new Date(form.when)),
      ...(fix ? { gps_lat: fix.lat, gps_lng: fix.lng } : {}),
      ...(form.taxon_name.trim() ? { taxon_name: form.taxon_name.trim() } : {}),
      ...(form.location_name.trim() ? { location_name: form.location_name.trim() } : {}),
    });
  };
  return (
    <form onSubmit={submit} style={S.card} data-testid="assign-panel" aria-labelledby="assign-title">
      <h3 id="assign-title" style={S.sectionTitle}>{t('assign.title')}</h3>
      <p style={S.hint}>{t('assign.intro', { code: specimen.specimen_code })}</p>
      <div style={S.row}>
        <label style={{ ...S.label, flex: '1 1 220px' }}>
          {t('fields.collected')}
          <input type="datetime-local" value={form.when} onChange={set('when')} style={S.input} title={t('register.tip.when')} />
        </label>
        <label style={{ ...S.label, flex: '2 1 200px' }}>
          {t('fields.taxon')}
          <input value={form.taxon_name} onChange={set('taxon_name')} style={S.input} title={t('tip.taxon')} />
        </label>
        <label style={{ ...S.label, flex: '2 1 200px' }}>
          {t('fields.locationName')}
          <input value={form.location_name} onChange={set('location_name')} style={S.input} title={t('tip.locationName')} />
        </label>
      </div>
      {error && <p style={S.error} role="alert">{error}</p>}
      <button type="submit" disabled={busy} style={{ ...S.btn(), marginTop: 12 }} title={t('assign.tip.submit')}
        data-testid="assign-submit">{busy ? t('saving') : t('assign.submit')}</button>
    </form>
  );
}
